import { useFrame } from "@react-three/fiber";
import { audioBus, computeBands } from "../audioBus";

/**
 * A mesh-less node that lives inside the Canvas so it can sample the audio on
 * the render clock (same rAF as everything else, and it naturally pauses when
 * the tab is idle). Each frame it reads the analyser, applies a fast-attack /
 * slow-release envelope + a simple bass beat detector, and writes the result
 * into `audioBus` for the shaders + scene to read. Renders nothing.
 *
 * NOTE: this deliberately does NOT touch the bloom effect. Driving a
 * postprocessing effect via a React ref crashes under React 19 — `ref` leaks
 * into props and @react-three/postprocessing JSON.stringifies effect props
 * (circular THREE objects → "Converting circular structure to JSON" → context
 * loss). The bloom already pulses for free because the shader brightens points
 * with the audio, so the composer's luminance pass catches more.
 */
export function AudioSampler() {
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 1 / 20);
    const b = audioBus;
    const v = b.values;
    if (b.replayFeed) return; // a flight tape is driving the values — don't fight it

    if (b.analyser && b.freq) {
      b.analyser.getByteFrequencyData(b.freq);
      if (b.wave) b.analyser.getByteTimeDomainData(b.wave); // for waveform visualizers
      const inst = computeBands(b.freq, b.sampleRate);
      const g = b.gain;
      // fast to rise, slower to fall — punchy but not jittery
      const follow = (prev: number, tgt: number) => {
        const k = tgt > prev ? Math.min(1, dt * 20) : Math.min(1, dt * 6);
        return prev + (tgt - prev) * k;
      };
      v.bass = follow(v.bass, Math.min(1, inst.bass * g));
      v.mid = follow(v.mid, Math.min(1, inst.mid * g));
      v.treble = follow(v.treble, Math.min(1, inst.treble * g));
      v.level = follow(v.level, Math.min(1, inst.level * g));

      // beat: bass jumping well above its running average, with a refractory gap
      b.bassAvg += (inst.bass - b.bassAvg) * Math.min(1, dt * 1.5);
      b.beatCooldown = Math.max(0, b.beatCooldown - dt);
      if (inst.bass > b.bassAvg * 1.4 && inst.bass > 0.16 && b.beatCooldown <= 0) {
        v.beat = 1;
        b.beatCooldown = 0.12;
      } else {
        v.beat = Math.max(0, v.beat - dt * 3.5);
      }
    } else if (v.level > 0.001 || v.bass > 0.001 || v.beat > 0.001) {
      // disconnected: decay everything back to rest
      const d = Math.max(0, 1 - dt * 3);
      v.level *= d;
      v.bass *= d;
      v.mid *= d;
      v.treble *= d;
      v.beat = Math.max(0, v.beat - dt * 4);
    }
  });

  return null;
}
